import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { asset, tenant } from '../../config/tenant';
import HomeContactForm from './components/HomeContactForm';
import HomeLoginCard from './components/HomeLoginCard';
import {
	ArrowIcon,
	CloseIcon,
	ConnectionsIcon,
	MarketIcon,
	ShieldIcon,
	WhatsAppDot,
	WhatsAppIcon,
} from './components/icons';
import SiteFooter from './components/SiteFooter';
import SiteHeader from './components/SiteHeader';
import { HomeRoot } from './PublicHomePage.styles';
import { WHATSAPP_MESSAGES, whatsappFormatted, whatsappLink } from './whatsapp';

const HEADER_OFFSET = 80;

const img = (file: string) => asset(`/images/home/${file}`);

/**
 * Home pública do Canal do Repasse (redesign aprovado), exibida a visitantes
 * não autenticados quando tenant.auth.landing === 'home'.
 *
 * Login, cadastro (/cadastro) e contato estão integrados aos componentes/serviços
 * já existentes na SPA. Ver HomeLoginCard e HomeContactForm.
 */
const PublicHomePage: React.FC = () => {
	const location = useLocation();
	const [waOpen, setWaOpen] = useState(false);

	const accent = tenant.colors.accent;
	const avaliadorUrl = tenant.links?.avaliadorDigital;

	const scrollTo = useCallback((id: string) => {
		const el = document.getElementById(id);
		if (!el) return;

		const y = el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
		window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });

		if (id === 'login') {
			const first = el.querySelector<HTMLInputElement>('input');
			first?.focus({ preventScroll: true });
		}
	}, []);

	/** Links de âncora: rolagem suave respeitando o header fixo. */
	const anchor = (id: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault();
		scrollTo(id);
	};

	// /login e #hash levam direto à seção correspondente (ex.: retorno do cadastro).
	useEffect(() => {
		const hash = location.hash.replace('#', '');
		const target = location.pathname.replace(/\/+$/, '').endsWith('/login') ? 'login' : hash;
		if (!target) return;

		const timer = window.setTimeout(() => scrollTo(target), 50);
		return () => window.clearTimeout(timer);
	}, [location.pathname, location.hash, scrollTo]);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setWaOpen(false);
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, []);

	return (
		<HomeRoot>
			<SiteHeader
				scrollTo={scrollTo}
				secondary={
					<Link className='cr-cad' to='/cadastro'>
						Cadastre-se
					</Link>
				}
				cta={
					<a className='cr-btn cr-btn--sm' href='#login' onClick={anchor('login')}>
						Acessar Plataforma
					</a>
				}
			/>

			<main>
				{/* ===================== HERO ===================== */}
				<section id='inicio' className='cr-hero'>
					<div className='cr-hero__glow' aria-hidden='true' />
					<div className='cr-container cr-hero__grid'>
						<div className='cr-hero__copy'>
							<span className='cr-eyebrow'>Marketplace automotivo B2B</span>
							<h1 className='cr-hero__title'>
								Conectamos
								<br />
								oportunidades.
								<br />
								Impulsionamos <span className='cr-or'>negócios.</span>
							</h1>
							<p className='cr-hero__text'>
								Uma plataforma digital que conecta concessionárias e lojistas, ampliando o acesso a
								oportunidades comerciais e proporcionando mais eficiência às negociações de veículos.
							</p>
							<div className='cr-hero__actions'>
								<Link className='cr-btn' to='/cadastro'>
									Cadastrar Minha Empresa
									<ArrowIcon />
								</Link>
								<a className='cr-link-ul' href='#solucoes' onClick={anchor('solucoes')}>
									<span>Conheça a Plataforma</span>
									<ArrowIcon stroke={accent} />
								</a>
							</div>
						</div>

						<div className='cr-hero__media'>
							<picture>
							<source srcSet={img('hero-smartphone.png')} />
								<img
									src={img('hero-smartphone.png')}
									alt={`Mão segurando smartphone com o aplicativo ${tenant.appName}`}
									width={316}
									height={441}
								/>
							</picture>
						</div>

						<HomeLoginCard />
					</div>
				</section>

				{/* ===================== MARCAS ===================== */}
				<section className='cr-brands' aria-label='Marcas presentes no mercado automotivo'>
					<div className='cr-container cr-brands__in'>
						<span className='cr-brands__caption'>
							Marcas presentes
							<br />
							no mercado automotivo
						</span>
						<span className='cr-brands__sep' aria-hidden='true' />
						<div className='cr-brands__strip'>
							<img
								src={img('marcas.png')}
								alt='Fiat, Volkswagen, Chevrolet, Toyota, Jeep, Renault, Nissan, Hyundai, Honda, Ford e Mitsubishi'
								width={880}
								height={98}
							/>
						</div>
					</div>
				</section>

				{/* ===================== SOLUÇÕES ===================== */}
				<section id='solucoes' className='cr-solutions'>
					<div className='cr-container cr-solutions__in'>
						<div className='cr-solutions__head'>
							<div className='cr-stack'>
								<span className='cr-eyebrow'>Nossas soluções</span>
								<h2 className='cr-h2'>
									Tecnologia para diferentes etapas do{' '}
									<span className='cr-or'>negócio automotivo.</span>
								</h2>
							</div>
							<p className='cr-lead'>
								Duas soluções que se complementam para conectar empresas, avaliar veículos e apoiar
								decisões com mais segurança e eficiência.
							</p>
						</div>

						<article id='plataforma' className='cr-product cr-product--01'>
							<div className='cr-product__copy'>
								<span className='cr-product__idx'>01</span>
								<div className='cr-product__body'>
									<span className='cr-eyebrow cr-eyebrow--sm'>{tenant.appName}</span>
									<h3 className='cr-product__title'>Marketplace automotivo B2B</h3>
									<p className='cr-lead cr-lead--sm'>
										Um ambiente digital desenvolvido para conectar concessionárias e lojistas
										multimarcas, facilitando o acesso a oportunidades comerciais e proporcionando
										mais eficiência nas negociações de veículos.
									</p>
									<a className='cr-link-or' href='#login' onClick={anchor('login')}>
										<span>Conheça a Plataforma</span>
										<ArrowIcon />
									</a>
								</div>
							</div>
							<div className='cr-laptop-wrap'>
								<img
									src={img('notebook.png')}
									alt={`Notebook com a plataforma ${tenant.appName}: acesso exclusivo aos usuários cadastrados`}
									width={573}
									height={273}
									loading='lazy'
								/>
							</div>
						</article>

						<article className='cr-product cr-product--02'>
							<div className='cr-product__photo'>
								<img
									src={img('avaliador.jpg')}
									alt='Avaliação veicular com apoio de tecnologia'
									width={532}
									height={258}
									loading='lazy'
								/>
							</div>
							<div className='cr-product__copy'>
								<span className='cr-product__idx'>02</span>
								<div className='cr-product__body'>
									<span className='cr-eyebrow cr-eyebrow--sm'>Avaliador Digital</span>
									<h3 className='cr-product__title'>
										Avaliação veicular
										<br />
										com <span className='cr-or'>inteligência artificial.</span>
									</h3>
									<p className='cr-lead cr-lead--sm'>
										Uma solução tecnológica que utiliza inteligência artificial para analisar as
										condições do veículo, identificar avarias e gerar uma avaliação completa,
										apoiando a tomada de decisão com mais segurança e transparência.
									</p>
									{avaliadorUrl && (
										<a className='cr-link-or' href={avaliadorUrl} target='_blank' rel='noopener noreferrer'>
											<span>Conheça o Avaliador Digital</span>
											<ArrowIcon />
										</a>
									)}
								</div>
							</div>
						</article>
					</div>
				</section>

				{/* ===================== QUEM SOMOS ===================== */}
				<section id='como-funciona' className='cr-about'>
					<div className='cr-container cr-about__in'>
						<div className='cr-about__copy'>
							<span className='cr-eyebrow'>Quem somos</span>
							<h2 className='cr-h2 cr-h2--sm'>Experiência de mercado conectada à tecnologia.</h2>
							<p className='cr-lead cr-lead--sm'>
								O {tenant.appName} nasceu em Belo Horizonte, em 2015, a partir da experiência de
								profissionais com mais de duas décadas de atuação no mercado de seminovos.
							</p>
							<p className='cr-lead cr-lead--sm'>
								Desenvolvemos uma plataforma digital para aproximar concessionárias e lojistas,
								utilizando tecnologia para ampliar conexões comerciais e proporcionar mais eficiência
								às negociações de veículos.
							</p>
						</div>
						<div className='cr-values'>
							<div>
								<MarketIcon stroke={accent} />
								<b>Mercado automotivo</b>
								<span>Conhecimento prático e experiência em todas as etapas do negócio.</span>
							</div>
							<div>
								<ConnectionsIcon stroke={accent} />
								<b>Conexões estratégicas</b>
								<span>Aproximamos empresas para gerar oportunidades com mais agilidade.</span>
							</div>
							<div>
								<ShieldIcon stroke={accent} />
								<b>Segurança e transparência</b>
								<span>Tecnologia para um ambiente de negócios mais seguro e eficiente.</span>
							</div>
						</div>
					</div>
				</section>

				{/* ===================== CONTATO ===================== */}
				<section id='contato' className='cr-contact'>
					<div className='cr-container cr-contact__in'>
						<div className='cr-contact__copy'>
							<span className='cr-eyebrow'>Dúvidas e solicitações</span>
							<h2 className='cr-h2 cr-h2--sm'>
								Fale com <span className='cr-or'>nossa equipe.</span>
							</h2>
							<p className='cr-lead cr-lead--sm'>
								Estamos à disposição para esclarecer suas dúvidas e receber suas solicitações sobre
								cadastro, acesso e nossas soluções.
							</p>
							<a className='cr-wa-card' href={whatsappLink()} target='_blank' rel='noopener noreferrer'>
								<WhatsAppDot />
								<span className='cr-wa-card__body'>
									<b>Atendimento via WhatsApp</b>
									<small>Fale diretamente com nossa equipe.</small>
									<span className='cr-wa-card__phone'>
										{whatsappFormatted()}
										<ArrowIcon />
									</span>
								</span>
							</a>
						</div>

						<HomeContactForm />
					</div>
				</section>
			</main>

			<SiteFooter scrollTo={scrollTo} />

			{/* ===================== WHATSAPP FLUTUANTE ===================== */}
			<div
				className={`cr-wa-menu${waOpen ? ' is-open' : ''}`}
				id='cr-wa-menu'
				role='dialog'
				aria-label='Fale pelo WhatsApp'
				aria-hidden={!waOpen}
			>
				<div className='cr-wa-menu__head'>
					<span>Fale com o {tenant.appName}</span>
					<button
						type='button'
						className='cr-icon-btn cr-wa-menu__close'
						onClick={() => setWaOpen(false)}
						aria-label='Fechar'
					>
						<CloseIcon />
					</button>
				</div>
				<a
					className='cr-wa-opt'
					href={whatsappLink(WHATSAPP_MESSAGES.info)}
					target='_blank'
					rel='noopener noreferrer'
				>
					<b>Atendimento comercial</b>
					<span>Informações sobre a plataforma.</span>
				</a>
				<a
					className='cr-wa-opt'
					href={whatsappLink(WHATSAPP_MESSAGES.access)}
					target='_blank'
					rel='noopener noreferrer'
				>
					<b>Informações sobre acesso</b>
					<span>Como cadastrar e acessar as oportunidades.</span>
				</a>
			</div>
			<button
				type='button'
				className='cr-wa-fab'
				onClick={() => setWaOpen((v) => !v)}
				aria-label='Falar pelo WhatsApp'
				aria-expanded={waOpen}
				aria-controls='cr-wa-menu'
			>
				<WhatsAppIcon />
			</button>
		</HomeRoot>
	);
};

export default PublicHomePage;
